import { boot } from 'quasar/wrappers';
import axios from 'axios';
import { getUserToken } from 'boot/getUserToken';
import { onMounted, ref } from 'vue';
import { capacitorHttpAxiosAdapter } from 'mobile/capacitorHttpAxiosAdapter';
// Be careful when using SSR for cross-request state pollution
// due to creating a Singleton instance here;
// If any client changes this (global) instance, it might be a
// good idea to move this instance creation inside of the
// "export default () => {}" function below (which runs individually
// for each client)
const api = axios.create({
  baseURL: process.env.SERVER_URI,
  adapter: process.env.MOBILE_BUILD ? capacitorHttpAxiosAdapter : undefined,
});

api.interceptors.request.use(async (req) => {
  const token = getUserToken();
  req.headers.Authorization = `Bearer ${token}`;
  req.headers.Accept = 'application/json';

  return req;
});

api.interceptors.response.use(async (response) => response, (error) => {
  if (process.env.MOBILE_BUILD) {
    if (error.response?.status === 401 || error.status === 401) {
      window.location.href = '/#/auth/code';
    }
  }

  return Promise.reject(error);
});

api.graphQLRequest = (operationName, query, variables, headers) => api.post(
  '',
  {
    operationName,
    query,
    variables,
  },
  {
    baseURL: process.env.GRAPHQL_URI,
    headers,
  },
);

export default boot(({ app }) => {
  // for use inside Vue files (Options API) through this.$axios and this.$api
  app.config.globalProperties.$axios = axios;
  // ^ ^ ^ this will allow you to use this.$axios (for Vue Options API form)
  //       so you won't necessarily have to import axios in each vue file

  app.config.globalProperties.$api = api;
  // ^ ^ ^ this will allow you to use this.$api (for Vue Options API form)
  //       so you can easily perform requests against your app's API
});

const useApi = (props = null) => {
  const result = ref(props.defaultValue ? props.defaultValue : null);
  const error = ref(null);
  const loading = ref(false);

  const handleError = (res) => {
    if (typeof res.data?.errors === 'object') {
      return `Error: ${Object.values(res.data?.errors).flat().join(',')}`;
    }
    if (Array.isArray(res.data?.errors)) {
      return `Error: ${res.data?.errors?.join(',')}`;
    }

    return `Error: ${res.data?.errors || res.data?.error || res.status}`;
  };

  const request = (fn, { body = undefined, params = undefined }) => {
    loading.value = true;

    const queryParams = new URLSearchParams(params);
    const resultPath = params ? `${props.path}?${queryParams.toString()}` : props.path;

    return fn(resultPath, body, { validateStatus: () => true })
      .then((res) => {
        if (res.status === 429) {
          return null;
        }

        if (![200, 201].find((s) => s === res.status)) {
          throw new Error(handleError(res));
        }

        result.value = props.resolver ? props.resolver(res.data) : res.data;
        error.value = null;

        if (props.onDone) {
          props?.onDone(res.data);
        }

        return res.data;
      }).catch((e) => {
        error.value = e;
        result.value = null;

        if (props.onError) {
          props?.onError(e);
        }
      }).finally(() => {
        loading.value = false;

        if (props.onComplete) {
          props.onComplete();
        }
      });
  };

  const get = (params) => request(api.get, { params });

  const post = (body) => request(api.post, { body });

  const put = (body) => request(api.put, { body });

  const del = () => request(api.delete);

  onMounted(() => {
    if (props === null) {
      throw new Error('Parameters for useApi were not passed');
    }

    if (props?.immediately) {
      get();
    }
  });

  return { result, loading, error, get, post, put, del };
};

export { api, useApi };
